import React, { useEffect, useState } from 'react';
import { empApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Fingerprint, ShieldCheck, Loader2, ShieldOff, AlertTriangle, Copy, Info } from 'lucide-react';
import { toast } from 'sonner';
import { soportaBiometria, prepararCreacion, serializarRegistro } from '../../lib/webauthn';

const msgErr = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

// Detecta si estamos dentro de un WebView de Android (Gmail, apps con visor propio, etc.).
// WebAuthn está deshabilitado en estos contextos.
const esAndroidWebView = () => {
  const ua = navigator.userAgent || '';
  if (!/Android/.test(ua)) return false;
  // Marcador estándar de Android System WebView
  if (/; wv\)/.test(ua)) return true;
  // Apps conocidas que renderizan en WebView (Gmail, Yahoo, algunos correos)
  if (/(GSA|FBAV|FBAN|Instagram|Line|MicroMessenger|Snapchat)\//i.test(ua)) return true;
  // Chrome real trae "Chrome/xxx" y no "wv". Version/xxx + Chrome/xxx sin wv sigue siendo Chrome/WebView.
  return false;
};

export default function BiometriaCard({ username }) {
  const [soportado, setSoportado] = useState(false);
  const [activa, setActiva] = useState(!!localStorage.getItem('empapp_bio_user'));
  const [trabajando, setTrabajando] = useState(false);
  const [webview] = useState(esAndroidWebView());
  const [diag, setDiag] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [precarga, setPrecarga] = useState(null); // opciones precargadas del backend

  useEffect(() => { soportaBiometria().then(setSoportado); }, []);

  // Precarga las opciones del backend en cuanto la tarjeta se muestra activa,
  // para que la llamada a navigator.credentials.create() ocurra dentro del gesto del usuario
  // (Android/Chrome rechaza con NotAllowedError si hay un fetch lento entre el click y create()).
  useEffect(() => {
    if (!soportado || activa || webview) return;
    let cancel = false;
    (async () => {
      try {
        const opts = await empApi.post('/empapp/webauthn/register/options', { origin: window.location.origin });
        if (!cancel) setPrecarga({ data: opts.data, ts: Date.now() });
      } catch { /* silencio, se reintenta en el click */ }
    })();
    return () => { cancel = true; };
  }, [soportado, activa, webview]);

  const copiarUrl = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success('Enlace copiado. Ábrelo en Chrome.'); }
    catch { toast.info(window.location.href); }
  };

  const copiarDiag = async () => {
    if (!diag) return;
    try { await navigator.clipboard.writeText(JSON.stringify(diag, null, 2)); toast.success('Diagnóstico copiado.'); }
    catch { toast.info('Copia manual: revisa el modal.'); }
  };

  const activar = async () => {
    setTrabajando(true);
    const t0 = performance.now();
    try {
      // 1) Usar opciones precargadas si están frescas (<4 min); si no, pedirlas ahora.
      let opts = null;
      if (precarga && (Date.now() - precarga.ts) < 4 * 60 * 1000) {
        opts = { data: precarga.data };
        setPrecarga(null); // consumida (el challenge es de un solo uso)
      } else {
        opts = await empApi.post('/empapp/webauthn/register/options', { origin: window.location.origin });
      }
      // 2) Llamar a la biometría inmediatamente (idealmente dentro del gesto del usuario).
      const cred = await navigator.credentials.create({ publicKey: prepararCreacion(opts.data) });
      // 3) Verificar en backend.
      await empApi.post('/empapp/webauthn/register/verify', { credential: serializarRegistro(cred), origin: window.location.origin });
      localStorage.setItem('empapp_bio_user', username);
      localStorage.removeItem('empapp_bio_dismiss');
      setActiva(true);
      toast.success('Huella activada. La próxima vez podrás entrar sin escribir el PIN.');
    } catch (err) {
      const dur = performance.now() - t0;
      const info = {
        error_name: err?.name || null,
        error_message: err?.message || null,
        duracion_ms: Math.round(dur),
        detalle_backend: err?.response?.data || null,
        user_agent: navigator.userAgent,
        origin: window.location.origin,
        secure_context: window.isSecureContext,
        display_mode: window.matchMedia?.('(display-mode: standalone)').matches ? 'standalone' : 'browser',
        webview_detectado: webview,
        opciones_precargadas: !!precarga,
      };
      console.warn('[Biometria] activacion fallida:', info);
      setDiag(info);
      // Reset precarga por si el challenge se consumió a medias
      setPrecarga(null);
      if (err?.name === 'NotAllowedError' && dur < 800) {
        toast.error(webview
          ? 'Abre el enlace en Chrome directamente. Desde el correo no se puede usar la huella.'
          : 'Tu dispositivo rechazó la activación al instante. Pulsa "Ver detalle" para más información.');
      } else if (err?.name === 'NotAllowedError') {
        toast.info('Activación cancelada. Vuelve a pulsar el botón para reintentar.');
      } else if (err?.name === 'InvalidStateError') {
        try { await empApi.delete('/empapp/webauthn/mias'); } catch { /* ignore */ }
        localStorage.removeItem('empapp_bio_user');
        setActiva(false);
        toast.error('Este dispositivo ya tenía una huella registrada. Hemos limpiado el estado — pulsa "Activar" otra vez.');
      } else if (err?.name === 'SecurityError') {
        toast.error('Error de seguridad: comprueba que accedes por HTTPS y siempre desde el mismo dominio (con o sin www, pero uno fijo).');
      } else if (err?.name === 'NotSupportedError') {
        toast.error('Este navegador no soporta el tipo de huella pedido. Prueba con Chrome, Safari o Edge actualizados.');
      } else {
        toast.error(msgErr(err, `No se pudo activar la huella (${err?.name || 'error'}). Pulsa "Ver detalle".`));
      }
    } finally { setTrabajando(false); }
  };

  const desactivar = async () => {
    setTrabajando(true);
    try {
      await empApi.delete('/empapp/webauthn/mias');
      localStorage.removeItem('empapp_bio_user');
      localStorage.removeItem('empapp_bio_dismiss');
      setActiva(false);
      toast.success('Huella desactivada en este dispositivo.');
    } catch (err) {
      toast.error(msgErr(err, 'No se pudo desactivar la huella.'));
    } finally { setTrabajando(false); }
  };

  if (!soportado) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3" data-testid="empapp-bio-card-nosop">
        <div className="flex items-start gap-3">
          <ShieldOff className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
          <div className="text-xs text-slate-400">Este dispositivo o navegador no soporta acceso con huella / Face ID.</div>
        </div>
      </div>
    );
  }

  if (webview) {
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="empapp-bio-card-webview">
        <div className="flex items-start gap-3 mb-2.5">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-sm text-amber-100 leading-tight">Ábrelo en Chrome para activar la huella</p>
            <p className="text-xs text-amber-200/80 mt-1">
              Has entrado desde un enlace del correo, y las apps de correo bloquean el acceso a la huella.
              Copia el enlace y ábrelo en Chrome (o instala primero la app con el botón azul de arriba y ábrela desde el icono).
            </p>
          </div>
        </div>
        <Button size="sm" onClick={copiarUrl} data-testid="empapp-bio-copiar-url"
          className="w-full bg-amber-500 hover:bg-amber-600 text-amber-950 h-9 text-xs font-semibold">
          <Copy className="h-4 w-4 mr-1.5" /> Copiar enlace para abrir en Chrome
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3" data-testid="empapp-bio-card">
      <div className="flex items-start gap-3 mb-2.5">
        <Fingerprint className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-bold text-sm text-slate-100 leading-tight">Acceso con huella / Face ID</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {activa
              ? 'Está activo en este dispositivo. Podrás entrar sin escribir el PIN.'
              : 'Actívalo y olvídate de teclear el PIN cada vez.'}
          </p>
        </div>
        {activa && <ShieldCheck className="h-5 w-5 text-emerald-400 shrink-0" />}
      </div>
      {activa ? (
        <Button size="sm" variant="outline" onClick={desactivar} disabled={trabajando} data-testid="empapp-bio-desactivar-btn"
          className="w-full bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 h-9 text-xs font-semibold">
          {trabajando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldOff className="h-4 w-4 mr-1.5" />}
          Desactivar huella en este dispositivo
        </Button>
      ) : (
        <Button size="sm" onClick={activar} disabled={trabajando} data-testid="empapp-bio-activar-btn"
          className="w-full bg-emerald-600 hover:bg-emerald-700 h-9 text-xs font-semibold">
          {trabajando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Fingerprint className="h-4 w-4 mr-1.5" />}
          Activar acceso con huella
        </Button>
      )}
      {diag && !activa && (
        <button onClick={() => setDiagOpen(true)}
          className="mt-2 w-full text-[11px] text-slate-500 hover:text-slate-300 underline flex items-center justify-center gap-1"
          data-testid="empapp-bio-ver-detalle">
          <Info className="h-3 w-3" /> Ver detalle del último error
        </button>
      )}

      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-slate-100">Diagnóstico de biometría</DialogTitle>
            <DialogDescription className="text-slate-400">Información técnica del último intento. Pásasela al administrador si el problema persiste.</DialogDescription>
          </DialogHeader>
          <pre className="text-[11px] bg-slate-950 border border-slate-800 rounded-md p-3 overflow-auto max-h-80 text-slate-300">
{diag ? JSON.stringify(diag, null, 2) : '(sin datos)'}
          </pre>
          <DialogFooter>
            <Button onClick={copiarDiag} data-testid="empapp-bio-copiar-diag" className="bg-indigo-500 hover:bg-indigo-600">
              <Copy className="h-4 w-4 mr-1.5" /> Copiar diagnóstico
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
